import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../hooks';
import { logout } from '../store/slices/authSlice';
import { ThemeToggle } from '../components/ThemeToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import {
  SubscriptionPlan,
  SubscriptionPlanCreateRequest,
  SubscriptionPlanUpdateRequest,
  PlanUsersResponse
} from '../types/api';
import './SubscriptionPlans.css';

const SubscriptionPlans: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [planUsers, setPlanUsers] = useState<PlanUsersResponse | null>(null);
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(null);

  // Form state
  const [formData, setFormData] = useState<SubscriptionPlanCreateRequest>({
    plan_name: '',
    price_monthly: 0,
    price_yearly: 0,
    chapter_limit: 0,
    ai_usage_limit: 0,
    storage_limit_mb: 250,
    max_file_size_mb: 15,
    features: {}
  });

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const isAdmin = await apiClient.verifyAdminAccess();
        if (!isAdmin) {
          navigate('/dashboard');
          return;
        }
        await loadPlans();
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/dashboard');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const plansData = await apiClient.getSubscriptionPlans();
      setPlans(plansData);
    } catch (error: any) {
      console.error('Error loading plans:', error);
      setError('Failed to load subscription plans');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlanUsers = async (planName: string) => {
    try {
      setActionLoading(`users-${planName}`);
      const users = await apiClient.getPlanUsers(planName);
      setPlanUsers(users);
      setShowUsersModal(true);
    } catch (error: any) {
      console.error('Error loading plan users:', error);
      setError('Failed to load plan users');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading('create');
      setError(null);

      await apiClient.createSubscriptionPlan(formData);
      setShowCreateModal(false);
      resetForm();
      await loadPlans();
    } catch (error: any) {
      console.error('Error creating plan:', error);
      setError('Failed to create subscription plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) return;

    try {
      setActionLoading('update');
      setError(null);

      const updateData: SubscriptionPlanUpdateRequest = {
        plan_name: formData.plan_name,
        price_monthly: formData.price_monthly,
        price_yearly: formData.price_yearly,
        chapter_limit: formData.chapter_limit,
        ai_usage_limit: formData.ai_usage_limit,
        storage_limit_mb: formData.storage_limit_mb,
        max_file_size_mb: formData.max_file_size_mb,
        features: formData.features,
        is_active: selectedPlan.is_active
      };

      await apiClient.updateSubscriptionPlan(selectedPlan.plan_id, updateData);
      setShowEditModal(false);
      setSelectedPlan(null);
      resetForm();
      await loadPlans();
    } catch (error: any) {
      console.error('Error updating plan:', error);
      setError('Failed to update subscription plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (plan: SubscriptionPlan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;

    try {
      setActionLoading(`delete-${planToDelete.plan_id}`);
      setError(null);

      await apiClient.deleteSubscriptionPlan(planToDelete.plan_id);
      setShowDeleteModal(false);
      setPlanToDelete(null);
      await loadPlans();
    } catch (error: any) {
      console.error('Error deleting plan:', error);
      setError('Failed to delete subscription plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setPlanToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      plan_name: '',
      price_monthly: 0,
      price_yearly: 0,
      chapter_limit: 0,
      ai_usage_limit: 0,
      storage_limit_mb: 250,
      max_file_size_mb: 15,
      features: {}
    });
  };

  const openEditModal = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setFormData({
      plan_name: plan.plan_name,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      chapter_limit: plan.chapter_limit,
      ai_usage_limit: plan.ai_usage_limit,
      storage_limit_mb: plan.storage_limit_mb ?? 250,
      max_file_size_mb: plan.max_file_size_mb ?? 15,
      features: plan.features || {}
    });
    setShowEditModal(true);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getPlanColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'free': return '#6b7280';
      case 'pro': return '#2563eb';
      case 'premium': return '#7c3aed';
      default: return '#059669';
    }
  };

  return (
    <div className="subscription-plans">
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>Subscription Plans</h1>
            <p>Manage pricing, features, and limits</p>
          </div>
          <div className="header-actions">
            <ThemeToggle />
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              + Create Plan
            </button>
            <Link to="/admin" className="btn btn-outline">
              Back to Admin
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="plans-main">
        {error && (
          <div className="alert alert-error">
            {error}
            <button
              className="alert-retry"
              onClick={loadPlans}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="loading-container">
            <LoadingSpinner size="lg" text="Loading subscription plans..." />
          </div>
        ) : (
          <div className="plans-grid">
            {plans.map(plan => (
              <div key={plan.plan_id} className="plan-card">
                <div className="plan-header">
                  <h3 className="plan-name" style={{ color: getPlanColor(plan.plan_name) }}>
                    {plan.plan_name.charAt(0).toUpperCase() + plan.plan_name.slice(1)}
                  </h3>
                  <div className="plan-status">
                    <span className={`status-badge ${plan.is_active ? 'active' : 'inactive'}`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="plan-pricing">
                  <div className="price-item">
                    <span className="price">{formatCurrency(plan.price_monthly)}</span>
                    <span className="period">/month</span>
                  </div>
                  <div className="price-item">
                    <span className="price">{formatCurrency(plan.price_yearly)}</span>
                    <span className="period">/year</span>
                  </div>
                </div>

                <div className="plan-limits">
                  <div className="limit-item">
                    <span className="limit-label">Chapters:</span>
                    <span className="limit-value">
                      {plan.chapter_limit === -1 ? 'Unlimited' : plan.chapter_limit}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">AI Usage:</span>
                    <span className="limit-value">
                      {plan.ai_usage_limit === -1 ? 'Unlimited' : plan.ai_usage_limit}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">Storage:</span>
                    <span className="limit-value">
                      {plan.storage_limit_mb != null ? `${plan.storage_limit_mb} MB` : 'Not set'}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">Max File:</span>
                    <span className="limit-value">
                      {plan.max_file_size_mb != null ? `${plan.max_file_size_mb} MB` : 'Not set'}
                    </span>
                  </div>
                </div>

                <div className="plan-features">
                  {Object.entries(plan.features || {}).map(([key, value]) => (
                    <div key={key} className="feature-item">
                      <span className="feature-key">{key}:</span>
                      <span className="feature-value">{String(value)}</span>
                    </div>
                  ))}
                </div>

                <div className="plan-meta">
                  <div className="meta-item">
                    <span className="meta-label">Created:</span>
                    <span className="meta-value">
                      {new Date(plan.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Updated:</span>
                    <span className="meta-value">
                      {new Date(plan.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="plan-actions">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => loadPlanUsers(plan.plan_name)}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === `users-${plan.plan_name}` ? 'Loading...' : 'View Users'}
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => openEditModal(plan)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteClick(plan)}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === `delete-${plan.plan_id}` ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Plan Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Subscription Plan</h2>
              <button
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreatePlan} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="plan_name">Plan Name</label>
                  <input
                    type="text"
                    id="plan_name"
                    value={formData.plan_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, plan_name: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price_monthly">Monthly Price ($)</label>
                  <input
                    type="number"
                    id="price_monthly"
                    step="0.01"
                    value={formData.price_monthly}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price_yearly">Yearly Price ($)</label>
                  <input
                    type="number"
                    id="price_yearly"
                    step="0.01"
                    value={formData.price_yearly}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chapter_limit">Chapter Limit (-1 for unlimited)</label>
                  <input
                    type="number"
                    id="chapter_limit"
                    value={formData.chapter_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, chapter_limit: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ai_usage_limit">AI Usage Limit (-1 for unlimited)</label>
                  <input
                    type="number"
                    id="ai_usage_limit"
                    value={formData.ai_usage_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, ai_usage_limit: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="storage_limit_mb">Storage Limit (MB)</label>
                  <input
                    type="number"
                    id="storage_limit_mb"
                    value={formData.storage_limit_mb}
                    onChange={(e) => setFormData(prev => ({ ...prev, storage_limit_mb: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="max_file_size_mb">Max File Size (MB)</label>
                  <input
                    type="number"
                    id="max_file_size_mb"
                    value={formData.max_file_size_mb}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_file_size_mb: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={!!actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!!actionLoading}
                >
                  {actionLoading === 'create' ? <LoadingSpinner size="sm" color="white" /> : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditModal && selectedPlan && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Subscription Plan</h2>
              <button
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdatePlan} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="edit_plan_name">Plan Name</label>
                  <input
                    type="text"
                    id="edit_plan_name"
                    value={formData.plan_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, plan_name: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_price_monthly">Monthly Price ($)</label>
                  <input
                    type="number"
                    id="edit_price_monthly"
                    step="0.01"
                    value={formData.price_monthly}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_price_yearly">Yearly Price ($)</label>
                  <input
                    type="number"
                    id="edit_price_yearly"
                    step="0.01"
                    value={formData.price_yearly}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_chapter_limit">Chapter Limit (-1 for unlimited)</label>
                  <input
                    type="number"
                    id="edit_chapter_limit"
                    value={formData.chapter_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, chapter_limit: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_ai_usage_limit">AI Usage Limit (-1 for unlimited)</label>
                  <input
                    type="number"
                    id="edit_ai_usage_limit"
                    value={formData.ai_usage_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, ai_usage_limit: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_storage_limit_mb">Storage Limit (MB)</label>
                  <input
                    type="number"
                    id="edit_storage_limit_mb"
                    value={formData.storage_limit_mb}
                    onChange={(e) => setFormData(prev => ({ ...prev, storage_limit_mb: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_max_file_size_mb">Max File Size (MB)</label>
                  <input
                    type="number"
                    id="edit_max_file_size_mb"
                    value={formData.max_file_size_mb}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_file_size_mb: parseInt(e.target.value) || 0 }))}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={!!actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!!actionLoading}
                >
                  {actionLoading === 'update' ? <LoadingSpinner size="sm" color="white" /> : 'Update Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plan Users Modal */}
      {showUsersModal && planUsers && (
        <div className="modal-overlay" onClick={() => setShowUsersModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Users on {planUsers.plan_name} Plan</h2>
              <button
                className="close-btn"
                onClick={() => setShowUsersModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="users-summary">
                <p><strong>Total Users:</strong> {planUsers.total_users.toLocaleString()}</p>
              </div>

              {planUsers.recent_users.length > 0 && (
                <div className="recent-users">
                  <h3>Recent Subscribers</h3>
                  <div className="users-list">
                    {planUsers.recent_users.map(user => (
                      <div key={user.user_id} className="user-item">
                        <div className="user-info">
                          <div className="user-name">{user.username}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                        <div className="user-date">
                          {new Date(user.subscribed_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && planToDelete && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal modal-delete" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Delete Subscription Plan</h2>
              <button
                className="close-btn"
                onClick={handleCancelDelete}
                disabled={!!actionLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p className="delete-warning">
                Are you sure you want to delete the <strong>"{planToDelete.plan_name}"</strong> subscription plan?
              </p>
              <p className="delete-warning-note">
                This action cannot be undone. Users on this plan may be affected.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelDelete}
                disabled={!!actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                disabled={!!actionLoading}
              >
                {actionLoading === `delete-${planToDelete.plan_id}` ? <LoadingSpinner size="sm" color="white" /> : 'Delete Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;